import { useQuery } from '@tanstack/react-query';
import { TrashIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import {
  WORKBOOK_ACTION_BINDINGS_MAX,
  parseWorkbookActionBindings,
  withWorkbookActionBinding,
  withoutWorkbookActionBinding,
} from '@erp/workflows';
import { orpc } from 'src/api/rpc/client';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';

/**
 * Workbooks attached to this agent (`config.workbookActions`): each becomes a
 * tool that runs that Workbook, with its inline tables as parameters. The
 * picker lists your own Workbooks; the agent runs them as its user, or as
 * you when it runs without one.
 */
export function WorkbookActionToolsSection({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const gt = useGT();
  const workbooks = useQuery(orpc.workbooks.list.queryOptions({ input: {} }));
  const bindings = parseWorkbookActionBindings(config);
  const names = new Map(
    (workbooks.data ?? []).map((workbook) => [workbook.id, workbook.name]),
  );
  const attached = new Set(bindings.map((binding) => binding.workbookId));
  const available = (workbooks.data ?? []).filter(
    (workbook) => !attached.has(workbook.id),
  );
  const atCap = bindings.length >= WORKBOOK_ACTION_BINDINGS_MAX;
  return (
    <div className="space-y-2 p-2">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs font-semibold">
          <T context="agent tools tab section for workbooks the agent can run">
            Workbooks
          </T>
        </h4>
        <Badge variant="secondary" className="text-xs">
          {bindings.length}
        </Badge>
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        <T context="explains that attached workbooks become tools of this agent">
          Every task this agent runs can run these workbooks and read their
          results.
        </T>
      </p>
      <ul className="space-y-1">
        {bindings.map((binding) => {
          const label =
            names.get(binding.workbookId) ??
            gt('A workbook you can’t open', {
              context:
                'attached workbook the current user does not own or that was deleted',
            });
          return (
            <li
              key={binding.workbookId}
              className="flex items-center gap-1 rounded-md border px-2 py-1.5"
            >
              <p className="min-w-0 flex-1 truncate text-xs font-medium">
                {label}
              </p>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                aria-label={gt('Remove {name}', {
                  context: 'remove a workbook tool from an agent',
                  name: label,
                })}
                onClick={() =>
                  onChange(
                    withoutWorkbookActionBinding(config, binding.workbookId),
                  )
                }
              >
                <TrashIcon className="h-3.5 w-3.5 text-destructive" />
              </Button>
            </li>
          );
        })}
      </ul>
      {!atCap && available.length > 0 && (
        <Select
          key={bindings.map((binding) => binding.workbookId).join(',')}
          onValueChange={(workbookId) =>
            onChange(withWorkbookActionBinding(config, workbookId))
          }
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue
              placeholder={gt('Add a workbook', {
                context: 'placeholder to attach a workbook tool to an agent',
              })}
            />
          </SelectTrigger>
          <SelectContent>
            {available.map((workbook) => (
              <SelectItem key={workbook.id} value={workbook.id}>
                {workbook.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
